// ─────────────────────────────────────────────────────────────────────────────
// agents — canonical desktop catalog for Phase 14B Chunk 6.
//
// Mirrors current desktop semantics EXACTLY (Ghost Flag 7): rosters are taken
// byte-for-byte from AgentSidebar.domainAgents / serviceAgents / codeTeamAgents;
// surfacing is NOT broadened. Agent ids are lowercase (the value POSTed to
// /api/agent by handleAgentChange); labels are UPPERCASE (the existing UI form).
// CEO sentinel = null (existing convention).
//
// Domain accents mirror mobile/rexy-ios/src/lib/agents.ts DOMAIN_ACCENTS for
// every domain shared with iOS. FRONTIER is desktop-only — assigned a neutral
// accent that matches iOS's pattern for non-distinguished domains.
// ─────────────────────────────────────────────────────────────────────────────

export interface AgentDef {
  id: string; // lowercase canonical id — matches handleAgentChange POST body
  label: string; // UPPERCASE UI label — matches existing AgentSidebar buttons
}

export interface DomainDef {
  id: string; // UPPERCASE domain key — matches activeDomain values
  label: string; // UPPERCASE UI label
  accent: string; // locked palette source (iOS DOMAIN_ACCENTS.accent)
  accentMuted: string; // iOS DOMAIN_ACCENTS.accentMuted — bubble fill for user messages
  agents: AgentDef[]; // domain-native agents only — services + code team are separate
}

// Locked per-domain accent — iOS DOMAIN_ACCENTS verbatim; FRONTIER neutral.
const ACCENTS: Record<string, string> = {
  PERSONAL: '#d4d4d4',
  TRADING: '#f59e0b',
  BUSINESS: '#d4d4d4',
  RESEARCH: '#d4d4d4',
  GROWTH: '#22c55e',
  FRONTIER: '#d4d4d4',
  CODE: '#0070f3',
  SECURITY: '#ef4444',
  MEDIA: '#d4d4d4',
};
// Locked per-domain muted accent — iOS DOMAIN_ACCENTS.accentMuted verbatim.
// Used as the iOS user-bubble fill. FRONTIER (desktop-only) inherits the
// neutral muted matching iOS PERSONAL/RESEARCH/BUSINESS.
const ACCENTS_MUTED: Record<string, string> = {
  PERSONAL: '#404040',
  TRADING: '#92400e',
  BUSINESS: '#404040',
  RESEARCH: '#404040',
  GROWTH: '#14532d',
  FRONTIER: '#404040',
  CODE: '#1e3a5f',
  SECURITY: '#7f1d1d',
  MEDIA: '#404040',
};

const a = (id: string, label: string): AgentDef => ({ id, label });

// Domain rosters — mirror current AgentSidebar.domainAgents byte-for-byte (Flag 7).
// Order matches the existing DomainBar.domains array so the bottom bar pill order
// is identical to today's top bar.
export const DOMAINS: DomainDef[] = [
  {
    id: 'PERSONAL',
    label: 'PERSONAL',
    accent: ACCENTS.PERSONAL,
    accentMuted: ACCENTS_MUTED.PERSONAL,
    agents: [a('spencer', 'SPENCER'), a('truffle', 'TRUFFLE'), a('tweety', 'TWEETY')],
  },
  {
    id: 'TRADING',
    label: 'TRADING',
    accent: ACCENTS.TRADING,
    accentMuted: ACCENTS_MUTED.TRADING,
    agents: [a('arthur', 'ARTHUR'), a('mack', 'MACK'), a('buckly', 'BUCKLY'), a('wallace', 'WALLACE')],
  },
  {
    id: 'BUSINESS',
    label: 'BUSINESS',
    accent: ACCENTS.BUSINESS,
    accentMuted: ACCENTS_MUTED.BUSINESS,
    agents: [a('stratiq', 'STRATIQ')],
  },
  {
    id: 'RESEARCH',
    label: 'RESEARCH',
    accent: ACCENTS.RESEARCH,
    accentMuted: ACCENTS_MUTED.RESEARCH,
    agents: [], // services-only (Flag 7: current behaviour preserved)
  },
  {
    id: 'GROWTH',
    label: 'GROWTH',
    accent: ACCENTS.GROWTH,
    accentMuted: ACCENTS_MUTED.GROWTH,
    agents: [
      a('alpha', 'ALPHA'),
      a('delta', 'DELTA'),
      a('echo', 'ECHO'),
      a('doe', 'DOE'),
      a('ilsa', 'ILSA'),
      a('buck', 'BUCK'),
    ],
  },
  {
    id: 'FRONTIER',
    label: 'FRONTIER',
    accent: ACCENTS.FRONTIER,
    accentMuted: ACCENTS_MUTED.FRONTIER,
    agents: [], // services-only (Flag 7: current behaviour preserved)
  },
  {
    id: 'CODE',
    label: 'CODE',
    accent: ACCENTS.CODE,
    accentMuted: ACCENTS_MUTED.CODE,
    agents: [a('sparticus', 'SPARTICUS'), a('ghost', 'GHOST'), a('joint', 'JOINT')],
  },
  {
    id: 'SECURITY',
    label: 'SECURITY',
    accent: ACCENTS.SECURITY,
    accentMuted: ACCENTS_MUTED.SECURITY,
    agents: [
      a('santi', 'SANTI'),
      a('warden', 'WARDEN'),
      a('cipher', 'CIPHER'),
      a('scout', 'SCOUT'),
      a('aegis', 'AEGIS'),
      a('trace', 'TRACE'),
      a('proxy', 'PROXY'),
    ],
  },
  {
    id: 'MEDIA',
    label: 'MEDIA',
    accent: ACCENTS.MEDIA,
    accentMuted: ACCENTS_MUTED.MEDIA,
    agents: [
      a('hammond', 'HAMMOND'),
      a('sawyer', 'SAWYER'),
      a('marty', 'MARTY'),
      a('dawson', 'DAWSON'),
      a('heisenberg', 'HEISENBERG'),
      a('stackhouse', 'STACKHOUSE'),
    ],
  },
];

// Service agents — always visible across every domain (Flag 7: current behaviour).
export const SERVICE_AGENTS: AgentDef[] = [
  a('grant', 'GRANT'),
  a('mcgarrett', 'MCGARRETT'),
  a('jesse', 'JESSE'),
];

// Code team — surfaced cross-domain, EXCLUDED from the domains listed below
// (Flag 7: current behaviour — NOT broadened to "everywhere").
export const CODE_TEAM: AgentDef[] = [
  a('sparticus', 'SPARTICUS'),
  a('ghost', 'GHOST'),
  a('joint', 'JOINT'),
];
export const CODE_TEAM_EXCLUDED_DOMAINS = ['CODE', 'FRONTIER', 'RESEARCH', 'SECURITY'] as const;

// CEO sentinel — handleAgentChange(null) on backend (existing convention).
export const CEO_AGENT_ID = null;

export function getDomain(id: string): DomainDef | undefined {
  return DOMAINS.find((d) => d.id === id);
}
